import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import CustomInput from "../../common/CustomInput";
import Button from "../../common/Button";
import {
  insurancePlanOptions,
  orderOfInsuranceOptions,
  provincialInsuranceOptions,
  stateOptions,
} from "./options";
import Collpsable from "../../common/Collpsable";
import {
  isFieldError,
  isHealthCareError,
} from "../../../helpers/input-validations";
import Alert from "../../common/Alert";
import { checkDuplicatePatient } from "../../../redux/modules/patient/actions";
import ConfirmationModalNew from "../../common/ConfirmationModalNew";
import { scrollY, formatToYYYY_MMM_DD, capitalizeFirstLetter } from "../../../helpers/functions";

const defaultInsurancePlan = {
  insurancePlanName: "",
  otherInsurancePlanName: "",
  orderOfInsurance: "",
  insuranceExpiryDate: "",
  insuranceEffectiveDate: "",
  comments: "",
  isEditing: false,
  isCompleted: false,
  errors: {
    insurancePlanName: "",
    orderOfInsurance: "",
    insuranceExpiryDate: "",
    insuranceEffectiveDate: "",
  },
};

const createDefaultInsurancePlan = () => ({
  ...defaultInsurancePlan,
  errors: { ...defaultInsurancePlan.errors },
});

const SecondStep = ({
  onClose,
  currentStep,
  setCurrentStep,
  patientCreateObject,
  setPatientCreateObject,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // deep cloning array to modify behaviour
  const provinceOptions = stateOptions.map((state) => {
    return { ...state, disabled: true };
  });
  provinceOptions.forEach((state) => {
    if (state.label === "Ontario") {
      state.disabled = false;
    }
  });

  const [provincialInsurance, setProvincialInsurance] = useState(
    patientCreateObject.provincialInsurance
  );
  const [provincialFieldDisabled, setProvincialFieldDisabled] = useState(false);
  const [openEndServiceModal, setOpenEndServiceModal] = useState(false);
  const [healthCardNumber, setHealthCardNumber] = useState(
    patientCreateObject.healthCardNumber
  );
  const [healthCardVersionCode, setHealthCardVersionCode] = useState(
    patientCreateObject.healthCardVersionCode
  );
  const [healthCardExpiryDate, setHealthCardExpiryDate] = useState(
    patientCreateObject.healthCardExpiryDate
  );
  const [healthCardProvince, setHealthCardProvince] = useState(
    patientCreateObject.healthCardProvince
  );
  const [healthCardErrors, setHealthCardErrors] = useState("");
  const [versionCodeErrors, setVersionCodeErrors] = useState("");
  const [existingUserId, setExistingUserId] = useState("");
  const [showDuplicatePatientDialog, setShowDuplicatePatientDialog] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [provinceErrors, setProvinceErrors] = useState("");
  const [showProvincialField, setShowProvincialField] = useState(true);
  const [insurancePlanNumber, setInsurancePlanNumber] = useState(0);
  const [insurancePlans, setInsurancePlans] = useState(
    patientCreateObject.insurancePlans && patientCreateObject.insurancePlans.length > 0
      ? patientCreateObject.insurancePlans
      : [createDefaultInsurancePlan()]
  );

  const suppressProvincialEffectRef = useRef(false);
  const getRequiredBg = (val) => {
  const isEmpty =
    val == null ||
    (typeof val === "string" && val.trim() === "") ||
    (Array.isArray(val) && val.length === 0);

  return isEmpty ? "!bg-blue-100" : "!bg-white";
};

  const isNoInsuranceSelected = (value) =>
    value === "noInsuranceNoConcent" || value === "noInsurancePaymentUnknown" || value === "noInsuranceConcent";

  const normalizeHealthCard = (value) =>
    String(value || "").replace(/-/g, "").trim();
  const normalizeVersionCode = (value) =>
    String(value || "")
      .replace(/_/g, "")
      .replace(/[^a-zA-Z]/g, "")
      .trim()
      .toUpperCase();

  const isProvincialSelected = provincialInsurance === "provincialInsurance";
  const hasProvincialInsuranceError = isFieldError(provincialInsurance);
  const hasHealthCardValidationError = isProvincialSelected && isHealthCareError(healthCardNumber);
  const hasVersionCodeValidationError = isProvincialSelected && isFieldError(normalizeVersionCode(healthCardVersionCode));
  const hasProvinceValidationError = isProvincialSelected && isFieldError(healthCardProvince);
  const isSaveAndContinueDisabled =
    isCheckingDuplicate || hasProvincialInsuranceError || hasHealthCardValidationError || hasVersionCodeValidationError || hasProvinceValidationError;

  const buildPatientUpdate = () => ({
    ...patientCreateObject,
    provincialInsurance,
    insurancePlans: insurancePlans?.filter((plan) => plan.isCompleted),
    healthCardNumber,
    healthCardVersionCode,
    healthCardExpiryDate,
    healthCardProvince,
  });

  // Validate insurance plans without mutating the original array.
  // Returns { isValid, plans: updatedPlans }
  const validateInsurancePlans = (plans = [], effectiveDate) => {
    let isInsuranceValidated = true;
    const updated = (plans || []).map((plan) => {
      const copy = { ...plan };
      if (copy.insurancePlanName === "" && copy.orderOfInsurance === "") {
        // leave as-is
      } else if (copy.insurancePlanName !== "" || copy.orderOfInsurance !== "") {
        if (
          validateField("insurancePlanName", copy.insurancePlanName) ||
          validateField("orderOfInsurance", copy.orderOfInsurance)
        ) {
          isInsuranceValidated = false;
        }
        copy.effectiveDate = effectiveDate.toLocaleDateString("en-CA");
      }
      return copy;
    });
    return { isValid: isInsuranceValidated, plans: updated };
  };

  // Helper: return true when the API-returned patient exactly matches
  // the input on first name, last name and date-of-birth (YYYY-MM-DD).
  const isExactDuplicate = (payload, dupResult) => {
    if (!payload || !dupResult || typeof dupResult !== "object") return false;
    const inputFirst = String(payload.firstName || "").trim().toLowerCase();
    const inputLast = String(payload.lastName || "").trim().toLowerCase();
    const inputDob = payload.dateOfBirth || null;

    const returned = dupResult.patient || dupResult;
    if (!returned) return false;
    const returnedFirst = String(returned.firstName || returned.firstname || "").trim().toLowerCase();
    const returnedLast = String(returned.lastName || returned.lastname || "").trim().toLowerCase();
    const returnedDobRaw = returned.dateOfBirth || returned.dob || returned.DateOfBirth || null;
    const returnedDob = returnedDobRaw ? String(returnedDobRaw).split("T")[0] : null;

    return (
      !!returnedFirst &&
      !!returnedLast &&
      !!returnedDob &&
      returnedFirst === inputFirst &&
      returnedLast === inputLast &&
      returnedDob === inputDob
    );
  };

  // duplicate detection is handled by the API; client only sends sanitized values

  const validateField = (field, value) => {
    let errorMessage = false;

    if (field === "insurancePlanName") {
      // Perform validation for insurance plan name
      // Example validation:
      if (value.trim() === "") {
        errorMessage = true;
      }
    } else if (field === "orderOfInsurance") {
      // Perform validation for order of insurance
      // Example validation:
      if (value.trim() === "") {
        errorMessage = true;
      }
    }

    return errorMessage;
  };

  useEffect(() => {
    // Skip clearing user-entered health-card/insurance once when `provincialInsurance` is cleared programmatically via the modal.
    if (suppressProvincialEffectRef.current) {
      suppressProvincialEffectRef.current = false;
      return;
    }

    if (provincialInsurance === "noInsuranceNoConcent") {
      setOpenEndServiceModal(true);
    }
    const isProvincial = provincialInsurance === "provincialInsurance";
    setProvincialFieldDisabled(!isProvincial);

    if (!isProvincial) {
      setHealthCardNumber("");
      setHealthCardVersionCode("");
      setHealthCardExpiryDate("");
      setHealthCardProvince("");
      setHealthCardErrors("");
      setVersionCodeErrors("");
      setProvinceErrors("");
      setInsurancePlans([createDefaultInsurancePlan()]);
    } else {
      // Insurance section remains visible when navigating back.
      if (!insurancePlans || insurancePlans.length === 0) {
        setInsurancePlans([createDefaultInsurancePlan()]);
      }
    }
  }, [provincialInsurance]);

  const updateErrors = (plans, index, key, value) => {
    // Treat an explicit empty/"none" selection as not an error while editing
    // (previous behaviour used the literal "none" so validateField would
    // not mark it as empty). This prevents the input border turning red
    // immediately when the user selects the 'none' option.
    const errorMessage = value === "" ? false : validateField(key, value);
    plans[index].errors[key] = errorMessage;
  };

  const provincialCollpsableDelete = () => {
    setHealthCardNumber("");
    setHealthCardVersionCode("");
    setHealthCardExpiryDate("");
  };

  const handleInputChange = (index, key, value) => {
    // Normalize incoming value: treat explicit 'none' selection as empty string
    const normalizedValue = value === "none" ? "" : value;
    const updatedPlans = [...insurancePlans];
    updatedPlans[index][key] = normalizedValue;

    // If user selects "NONE" for Insurance Plan Name, clear all related fields
    if (key === "insurancePlanName" && normalizedValue === "") {
      updatedPlans[index].orderOfInsurance = "";
      updatedPlans[index].otherInsurancePlanName = "";
      updatedPlans[index].insuranceExpiryDate = "";
      updatedPlans[index].insuranceEffectiveDate = "";
      updatedPlans[index].comments = "";
      
      // Clear errors for all cleared fields
      updatedPlans[index].errors.insurancePlanName = false;
      updatedPlans[index].errors.orderOfInsurance = false;
    }

    updateErrors(updatedPlans, index, key, normalizedValue);

    // Check and update isCompleted for the plan
    const isCompleted = Object.values(updatedPlans[index].errors).every(
      (error) => error === false
    );
    updatedPlans[index].isCompleted = isCompleted;
    setInsurancePlans(updatedPlans);
  };

  const addInsurancePlan = () => {
    const existingPlanWithErrors = insurancePlans.find(
      (plan) => !plan.isCompleted
    );
    if (!existingPlanWithErrors) {
      const updatedPlans = insurancePlans.map((plan) => {
        return { ...plan, isEditing: false };
      });

      updatedPlans.push({
        insurancePlanName: "",
        otherInsurancePlanName: "",
        orderOfInsurance: "",
        insuranceExpiryDate: "",
        insuranceEffectiveDate: "",
        comments: "",
        isEditing: true,
        isCompleted: false, 
        errors: {
          insurancePlanName: "",
          orderOfInsurance: "",
        },
      });
      setInsurancePlans(updatedPlans);
    } else {
      const updatedPlans = insurancePlans.map((plan) => {
        if (!plan.isCompleted) {
          return {
            ...plan,
            errors: {
              insurancePlanName: validateField(
                "insurancePlanName",
                plan.insurancePlanName
              ),
              orderOfInsurance: validateField(
                "orderOfInsurance",
                plan.orderOfInsurance
              ),
            },
          };
        }
        return plan;
      });
      setInsurancePlans(updatedPlans);
    }
  };

  const removeInsurancePlan = (index) => {
    const updatedPlans = [...insurancePlans];

    if (updatedPlans[index].isEditing) {
      // If the plan being removed is in editing mode, cancel editing first
      updatedPlans[index].isEditing = false;
    }

    updatedPlans.splice(index, 1);
    setInsurancePlans(updatedPlans);
  };

  const editPlan = (index) => {
    const updatedPlans = insurancePlans.map((plan, i) => ({
      ...plan,
      isEditing: i === index,
    }));
    setInsurancePlans(updatedPlans?.filter((plan) => plan.isCompleted));
  };

  const manageStep = async () => {
    const defaultEffectiveDate = new Date();
    const noInsuranceSelected = isNoInsuranceSelected(provincialInsurance);
    const healthCardError = noInsuranceSelected ? false : isHealthCareError(healthCardNumber);
    const versionCodeError = noInsuranceSelected ? false : isFieldError(normalizeVersionCode(healthCardVersionCode));
    const provinceError = noInsuranceSelected ? false : isFieldError(healthCardProvince);

    let validated = true;
    if (healthCardError || versionCodeError || provinceError) {
      setHealthCardErrors(healthCardError);
      setVersionCodeErrors(versionCodeError);
      setProvinceErrors(provinceError);
      setShowProvincialField(true);
      validated = false;
    }

    const { isValid: isInsuranceValidated, plans: validatedPlans } =
      validateInsurancePlans(insurancePlans, defaultEffectiveDate);

    // if validator returned normalized plans, update state (pure function)
    const plansChanged = JSON.stringify(validatedPlans) !== JSON.stringify(insurancePlans);
    if (plansChanged) setInsurancePlans(validatedPlans);

    if (validated && isInsuranceValidated) {
      // Run duplicate-check defensively before proceeding
      setIsCheckingDuplicate(true);
      const requestPayload = {
        firstName: String(patientCreateObject.firstName || "").trim(),
        lastName: String(patientCreateObject.lastName || "").trim(),
        dateOfBirth: patientCreateObject.dateOfBirth
          ? String(patientCreateObject.dateOfBirth).split("T")[0]
          : null,
      };

      setIsCheckingDuplicate(true);
      let dispatchResult = null;
      try {
        dispatchResult = await dispatch(checkDuplicatePatient(requestPayload));
      } finally {
        setIsCheckingDuplicate(false);
      }

      const duplicateResult = dispatchResult && dispatchResult.payload ? dispatchResult.payload : dispatchResult;

      const isDuplicate = !!(
        duplicateResult?.isDuplicate === true ||
        duplicateResult?.IsDuplicate === true ||
        duplicateResult?.patient?.isDuplicate === true ||
        duplicateResult?.patient?.IsDuplicate === true
      );

      const idFromResult = duplicateResult?.patient?.patientId || duplicateResult?.patient?.id || duplicateResult?.patientId || duplicateResult?.id || duplicateResult;

      const shouldShowDuplicate = isExactDuplicate(requestPayload, duplicateResult);

      if (shouldShowDuplicate) {
        const returnedPatient = duplicateResult && typeof duplicateResult === "object" ? duplicateResult.patient || duplicateResult : null;
        setExistingUserId(String(idFromResult || (returnedPatient && (returnedPatient.patientId || returnedPatient.id)) || ""));
        setShowDuplicatePatientDialog(true);
        return;
      }

      // No duplicate detected -> save and proceed
      setPatientCreateObject(buildPatientUpdate());
      setCurrentStep(currentStep + 1);
    }
  };
  return (
    <>
      <ConfirmationModalNew
        open={showDuplicatePatientDialog}
        title={"Possible Duplicate Patient"}
        type={"primary"}
        confirmText="Continue to Add Patient"
        cancelText="Review Existing Patient Chart"
        className="!w-[600px] !max-w-none"
        content={() => {
          const dob = patientCreateObject.dateOfBirth
            ? formatToYYYY_MMM_DD(patientCreateObject.dateOfBirth)
            : "N/A";
          const hcSource = healthCardNumber || patientCreateObject.healthCardNumber || "";
          const hc = hcSource ? String(hcSource).replace(/-/g, "") : "";
          const maskedHC = hc ? `xxxxxx${hc.slice(-4)}` : null;
          const nameParts = `${capitalizeFirstLetter(patientCreateObject.lastName || "")}${patientCreateObject.firstName ? `, ${capitalizeFirstLetter(patientCreateObject.firstName)}` : ""}${patientCreateObject.middleName ? `, ${capitalizeFirstLetter(patientCreateObject.middleName)}` : ""}`;
          return (
            <div className="text-gray-800 text-base">
              <p>
                This patient - <span className="text-teal-300 font-semibold">{nameParts}</span>, shares the same personal information as another patient in your pharmacy.
              </p>
              <br />
              <ul className="list-disc pl-6 text-gray-800">
                <li>
                  <span className="text-teal-300 font-semibold">Date of Birth</span>: {dob}
                </li>
                <li>
                  <span className="text-teal-300 font-semibold">Health Card</span>: {maskedHC ? maskedHC : "No Information"}
                </li>
              </ul>
              <p className="mt-4">Please verify the correct patient record.</p>
            </div>
          );
        }}
        onCross={() => {
          setShowDuplicatePatientDialog(false);
          setExistingUserId("");
        }}
        handleClose={() => {
          setShowDuplicatePatientDialog(false);
          setExistingUserId("");
        }}
        confirmAction={() => {
          setShowDuplicatePatientDialog(false);
          setExistingUserId("");
          setCurrentStep(currentStep + 1);
        }}
        cancelAction={() => {
          navigate("/patient/" + existingUserId);
        }}
      />

      <div
        className="flex flex-col px-8 justify-between flex-1 py-6 h-full overflow-y-auto"
        id="form-body"
      >
        <div className="grid grid-cols-12 gap-3">
          {!showProvincialField && (
            <Collpsable
              onEdit={() => setShowProvincialField(true)}
              onDelete={() => provincialCollpsableDelete()}
              className={"col-span-12 transition-all ease-in-out duration-500"}
              title={`Provincial - ${healthCardNumber} - ${healthCardVersionCode}`}
            />
          )}
          {insurancePlans
            .filter((plan) => plan.isCompleted)
            .map((plan, index) => {
              return (
                <div
                  className="grid grid-cols-12 col-span-12 gap-3"
                  key={index}
                >
                  {plan.isCompleted ? (
                    <Collpsable
                      onEdit={() => editPlan(index)}
                      onDelete={() => removeInsurancePlan(index)}
                      className={
                        "col-span-12 transition-all ease-in-out duration-500"
                      }
                      title={
                        insurancePlanOptions.find(
                          (item) => item.value === plan.insurancePlanName
                        )?.label || plan.insurancePlanName || "Insurance Plan"
                      }
                    />
                  ) : null}
                </div>
              );
            })}
          <>
            <p className="col-span-12 font-medium text-gray-900 text-body-md">
              Provincial Insurance
            </p>
            <CustomInput
              vertical
              className="col-span-12"
              options={provincialInsuranceOptions}
              onChange={(e) => setProvincialInsurance(e.target.value)}
              type="radio"
              name="statusOptions"
              value={provincialInsurance}
            />
          </>
          <>
            <CustomInput
              name="healthCard"
              className={`col-span-6 ${getRequiredBg(healthCardNumber)}`}
              label="Health Card"
              mask="9999-999-999"
              placeholder="XXXX-XXX-XXX"
              type="text"
              value={healthCardNumber}
              error={healthCardErrors}
              onChange={(e) => {
                setHealthCardNumber(e.target.value);
                setHealthCardErrors(isHealthCareError(e.target.value));
              }}
              disabled={provincialFieldDisabled}
              required
            />
            <CustomInput
              name="versionCode"
              className={`col-span-6 ${getRequiredBg(healthCardVersionCode)}`}
              label="Version Code"
              placeholder="ER"
              type="versionCode"
              value={healthCardVersionCode}
              disabled={provincialFieldDisabled}
              onChange={(e) => {
                const normalizedValue = normalizeVersionCode(e.target.value);
                setHealthCardVersionCode(normalizedValue);
                setVersionCodeErrors(
                  isFieldError(normalizedValue)
                );
              }}
              error={versionCodeErrors}
              required
            />
            <CustomInput
              name="expiryDate"
              className="col-span-6"
              label="Expiry Date"
              type="date"
              value={healthCardExpiryDate}
              disabled={provincialFieldDisabled}
              onChange={(value) => {
                setHealthCardExpiryDate(value);
              }}
            />
            <CustomInput
              options={provinceOptions}
              name="state"
              value={healthCardProvince}
              className={`col-span-6 ${getRequiredBg(healthCardProvince)}`}
              label="Province"
              type="dropdown"
              required
              error={provinceErrors}
              disabled={provincialFieldDisabled}
              onChange={(e) => {
                setProvinceErrors(isFieldError(e.value));
                setHealthCardProvince(e.value);
              }}
            />
            <div className="col-span-12 my-3 bg-gray-100 h-[1px] w-full " />
          </>
          <p className="col-span-12 font-medium text-gray-900 text-body-md">
            Third Party Insurance Plan(s) (Optional)
          </p>

          {insurancePlans.map((plan, index) => {
            return plan.isEditing || insurancePlans.length === 1 ? (
              <div className="grid grid-cols-12 col-span-12 gap-3" key={index}>
                <CustomInput
                  options={insurancePlanOptions}
                  name={`insurancePlanName_${index}`}
                  className="col-span-6"
                  label="Insurance Plan Name"
                  type="dropdown"
                  onFocus={() => setShowProvincialField(false)}
                  value={
                    insurancePlans[index].insurancePlanName?.length > 0
                      ? insurancePlans[index].insurancePlanName
                      : ""
                  }
                  error={insurancePlans[index].errors.insurancePlanName}
                  onChange={(e) =>
                    handleInputChange(index, "insurancePlanName", e.value)
                  }
                />
                <CustomInput
                  options={orderOfInsuranceOptions}
                  name={`orderOfInsurance_${index}`}
                  className="col-span-6"
                  label="Order of Insurance"
                  type="dropdown"
                  onFocus={() => setShowProvincialField(false)}
                  value={
                    insurancePlans[index].orderOfInsurance?.length > 0
                      ? insurancePlans[index].orderOfInsurance
                      : ""
                  }
                  error={insurancePlans[index].errors.orderOfInsurance}
                  onChange={(e) =>
                    handleInputChange(index, "orderOfInsurance", e.value)
                  }
                />
                {insurancePlans[index].insurancePlanName === "other" && (
                  <>
                    <CustomInput
                      name={`otherInsurancePlanName_${index}`}
                      className="col-span-12"
                      label="Other (Insurance Plan Name)"
                      placeholder={"Enter Name"}
                      inputClassName="!placeholder-gray-600"
                      type="text"
                      required
                      value={insurancePlans[index]?.otherInsurancePlanName}
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "otherInsurancePlanName",
                          e.value
                        )
                      }
                    />
                  </>
                )}
                <CustomInput
                  name={`insuranceExpiryDate_${index}`}
                  className="col-span-6"
                  label="Expiry Date"
                  onFocus={() => {
                    setShowProvincialField(false);
                    scrollY("form-body");
                  }}
                  type="date"
                  value={insurancePlans[index].insuranceExpiryDate}
                  error={insurancePlans[index].errors.insuranceExpiryDate}
                  onChange={(value) =>
                    handleInputChange(index, "insuranceExpiryDate", value)
                  }
                />
                <CustomInput
                  name={`insuranceEffectiveDate_${index}`}
                  className="col-span-6"
                  label="Effective Date"
                  onFocus={() => {
                    setShowProvincialField(false);
                    scrollY("form-body");
                  }}
                  type="date"
                  value={insurancePlans[index].insuranceEffectiveDate}
                  error={insurancePlans[index].errors.insuranceEffectiveDate}
                  onChange={(value) =>
                    handleInputChange(index, "insuranceEffectiveDate", value)
                  }
                />
                <div className="col-span-12 my-3 bg-gray-100 h-[1px] w-full " />
                <CustomInput
                  name={`comments_${index}`}
                  className="col-span-12"
                  label="Comments"
                  placeholder="Enter Note"
                  type="text"
                  value={insurancePlans[index].comments}
                  onChange={(e) =>
                    handleInputChange(index, "comments", e.target.value)
                  }
                />
              </div>
            ) : null;
          })}
          <div className="col-span-12">
            <Alert
              type="warning"
              title={"Billing"}
              hideCross={true}
              content={() =>
                "Direct billing not active in REEL at this time, therefore only enter Name of the Third Party Insurance Plan (optional). This may help to facilitate with Minor Ailment Prescribing / and Interventions."
              }
              isWrapped={true}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center py-6 space-x-5">
        <Button
          className="min-w-[200px]"
          text={currentStep === 1 ? `Cancel` : "Go Back"}
          type="secondary"
          onClick={() =>
            currentStep === 1 ? onClose(false) : setCurrentStep(currentStep - 1)
          }
        />
        <Button
          className="min-w-[200px]"
          text={"Save & Add Another"}
          type="primary"
          onClick={() => addInsurancePlan(insurancePlanNumber)}
        />
        <Button
          className="min-w-[200px]"
          text="Save & Continue"
          type="primary"
          disabled={isSaveAndContinueDisabled}
          onClick={() => manageStep()}
        />
      </div>

      <ConfirmationModalNew
        open={openEndServiceModal}
        title={"No Provincial Insurance"}
        type={"primary"}
        confirmText={"Choose to Pay"}
        cancelText="Cancel & Reselect"
        content={() => (
          <p>
            Patient does not consent to pay for Clinical Service. Refer to
            alternate healthcare provider.
          </p>
        )}
        onCross={() => {
          setOpenEndServiceModal(false);
        }}
        handleClose={() => {
          setOpenEndServiceModal(false);
        }}
        confirmAction={() => {
          setProvincialInsurance("noInsuranceConcent");
          setOpenEndServiceModal(false);
        }}
          cancelAction={() => {
            suppressProvincialEffectRef.current = true;
            setProvincialInsurance("");
            setOpenEndServiceModal(false);
          }}
      />
    </>
  );
};

export default SecondStep;
